<template>
  <Transition name="splash-fade">
    <div v-if="showSplash" class="app-splash">
      <div class="splash-logo" />
    </div>
  </Transition>

  <div
    v-if="showApp"
    id="gambits-app"
    :class="{
      'is-mobile': isMobile,
      onboarding: isAuthScreen,
      'is-localhost': isLocalhost,
      'is-modal-chat': isModalChatExperience,
    }"
    :style="{ '--bottom-offset': '0px' }"
  >
    <main id="gam-wrapper" :class="{ 'is-auth-screen': isAuthScreen }">
      <RouterView v-slot="{ Component }">
        <template v-if="Component">
          <KeepAlive :include="keepAliveIncludes">
            <component :is="Component" />
          </KeepAlive>
        </template>
      </RouterView>
    </main>
  </div>

  <ProfileLockModal />
  <GeoLocation />
  <ColorCycle />
  <GamConfetti />
  <GamUpdateBanner />
  <GamLiveBanner v-if="showApp" />
  <OfflineView v-if="!isOnline" />
  <ModalsContainer />
</template>

<script setup lang="ts">
import 'vue-final-modal/style.css';

import { gambits } from '@/core/gambits';
import { isMobile } from '@/core/helpers/ui.helper';
import { useAppStore } from '@/stores/app.store';
import { useAuthStore } from '@/stores/auth.store';
import { useGeolocationStore } from '@/stores/geolocation.store';
import { useNavigationStore } from '@/stores/navigation.store';
import ColorCycle from '@/views/components/ColorCycle.vue';
import GamLiveBanner from '@/views/components/gam-live-banner/GamLiveBanner.vue';
import GamConfetti from '@/views/components/GamConfetti.vue';
import GamUpdateBanner from '@/views/components/GamUpdateBanner.vue';
import { useActivityHeartbeat } from '@/views/composables/hooks/useActivityHeartbeat';

import GeoLocation from '@/GeoLocation.vue';
import ProfileLockModal from '@/views/main/players/ProfileLockModal.vue';
import OfflineView from '@/views/OfflineView.vue';
import { useHead, useSeoMeta } from '@unhead/vue';
import { storeToRefs } from 'pinia';
import { computed, nextTick, onMounted, onUnmounted, ref } from 'vue';
import { ModalsContainer } from 'vue-final-modal';
import { useRoute } from 'vue-router';
const isLocalhost = window.location.hostname === 'localhost';

const keepAliveIncludes = ['SparkIgnition', 'UsernameView'];

const isInitialEditAction = window.location.search.includes('action=edit');
const isInitialPlacesAction = window.location.search.includes('action=places');
const isInitialSparksRoute = window.location.pathname.includes('sparks');
// Guard against deep linking Sparks Edit & Places
const isInitialSparksEditOrPlacesLink = isInitialSparksRoute && (isInitialEditAction || isInitialPlacesAction);
if (isInitialSparksEditOrPlacesLink) {
  window.location.replace('/sparks');
}

useHead(gambits.getPageMeta());
useSeoMeta(gambits.useSeoMeta());

const appStore = useAppStore();
const { isOnline } = storeToRefs(appStore);
const authStore = useAuthStore();

const { beat: markActive } = useActivityHeartbeat();

const navigationStore = useNavigationStore();
const { isModalChatExperience } = storeToRefs(navigationStore);
const geolocationStore = useGeolocationStore();
const route = useRoute();

const SPLASH_DELAY_MS = 300;
const showApp = ref(false);
const showSplash = ref(false);
const language = ref('');
let splashTimer: ReturnType<typeof setTimeout> | null = null;

const isAuthScreen = computed(() => route.path.includes('/auth'));

onMounted(async () => {
  splashTimer = setTimeout(() => {
    showSplash.value = true;
  }, SPLASH_DELAY_MS);

  try {
    language.value = await gambits.localizationService.setTranslations();
    gambits.filterService.setAppFilters();
  } finally {
    init();
  }
});

/*
onMounted(async () => {
  if (!city.value) {
    await cityStore.determineCity();
  }
});
*/

onUnmounted(async () => {
  gambits.geolocationService.stopTracking();
});

const init = async () => {
  await authStore.initialize();
  await geolocationStore.initialize();

  if (splashTimer) {
    clearTimeout(splashTimer);
    splashTimer = null;
  }

  showApp.value = true;

  if (showSplash.value) {
    await nextTick();
    showSplash.value = false;
  }

  sessionStorage.setItem('gam_init', '1');
  markActive();
};
</script>

<style lang="scss" scoped>
.app-splash {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100svh;
  background: var(--color-dark-700);
  position: fixed;
  top: 0;
  left: 0;
  z-index: 99999;
}

.splash-fade-leave-active {
  transition: opacity 300ms ease-out;
}

.splash-fade-leave-to {
  opacity: 0;
}

.splash-logo {
  // Matches the pre-hydration splash in index.html, which must carry the same
  // numbers or the mark jumps when Vue mounts. Kept to the crown's own 67:32
  // ratio, and sized to the visual area the old mark had on an empty screen.
  width: 88px;
  height: 42px;
  background: var(--color-linear-gradient-skeleton);
  background-size: 200% 200%;
  animation: splash-shimmer 2s linear infinite;
  -webkit-mask-image: url('@/ui/images/splash-logo.svg');
  mask-image: url('@/ui/images/splash-logo.svg');
  -webkit-mask-size: contain;
  mask-size: contain;
  -webkit-mask-repeat: no-repeat;
  mask-repeat: no-repeat;
  -webkit-mask-position: center;
  mask-position: center;
}

@keyframes splash-shimmer {
  0% {
    background-position: 100% bottom;
  }
  100% {
    background-position: -100% bottom;
  }
}

#gambits-app {
  width: 100%;
  height: calc(var(--vh, 1svh) * 100);
  display: flex;
  flex-direction: row;
  overflow: hidden;
  align-items: stretch;
  justify-content: stretch;
  position: relative;
}

#gambits-app.is-modal-chat {
  position: fixed;
  left: 0;
  bottom: 0;
}

.gam-nav {
  order: 1;
}

main {
  order: 2;
}

#gam-wrapper {
  display: flex;
  flex-direction: column;
  flex: 1 0 0;
  align-items: stretch;
  justify-content: stretch;
  width: 100%;
  height: 100%;
  /* The live banner already covers the safe area the screens pad for themselves. */
  padding-top: max(0px, calc(var(--live-banner-height, 0px) - var(--sai-top, 0px)));
}

#gam-wrapper.is-auth-screen {
  height: fit-content;
}

// .is-mobile.is-nav-present #gam-wrapper {
//   height: calc(100% - 68px);
// }

.component-wrapper {
  padding: 0;
  flex-grow: 1;
  flex-shrink: 0 !important;
}

.git-hash {
  position: absolute;
  top: 10px;
  right: 20px;
  color: var(--color-white-10);
  z-index: 99999999;
  pointer-events: none;
  display: none;
}

.flex-grow-1 {
  flex-grow: 1 !important;
}
</style>
